import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner, Button } from '@telegram-apps/telegram-ui';
import { useStats, useRequestsByCategory, useRequests } from '../hooks/useRequests';
import { hapticFeedback } from '../lib/telegram';
import type { RequestStats, RequestDisplayItem } from '../lib/api';
import './HomePage.css';

// Category configuration
const CATEGORIES = [
  { id: 'needs_response', label: 'Требуют реакции', icon: '🚨' },
  { id: 'in_progress', label: 'В работе', icon: '⚙️' },
  { id: 'waiting', label: 'Ожидание', icon: '⏳' },
  { id: 'all', label: 'Все', icon: '📋' },
] as const;

// Счётчик пилюли должен совпадать с содержимым списка категории:
// 'waiting' на сервере включает и waitingCustomer, и waitingOther
function getCategoryCount(stats: RequestStats, categoryId: string): number {
  switch (categoryId) {
    case 'needs_response':
      return stats.needsResponse;
    case 'in_progress':
      return stats.inProgress;
    case 'waiting':
      return stats.waitingCustomer + stats.waitingOther;
    case 'all':
      return stats.total;
    default:
      return 0;
  }
}

// Status configuration
const STATUS_CONFIG: Record<string, { icon: string; color: string }> = {
  assigned: { icon: '📌', color: '#FF9F0A' },
  accepted: { icon: '✅', color: '#34C759' },
  in_progress: { icon: '⚙️', color: '#0A84FF' },
  inprogress: { icon: '⚙️', color: '#0A84FF' },
  waiting_for: { icon: '⏳', color: '#FFD60A' },
  waitingfor: { icon: '⏳', color: '#FFD60A' },
  waiting_for_customer: { icon: '⏸', color: '#FF9F0A' },
  waitingforcustomer: { icon: '⏸', color: '#FF9F0A' },
  completed: { icon: '✔️', color: '#34C759' },
  cancelled: { icon: '❌', color: '#FF453A' },
  declined: { icon: '🚫', color: '#FF453A' },
};

const IMPACT_CONFIG: Record<string, { icon: string; color: string }> = {
  top: { icon: '●', color: '#FF453A' },
  high: { icon: '●', color: '#FF9F0A' },
  medium: { icon: '●', color: '#FFD60A' },
  low: { icon: '●', color: '#34C759' },
  none: { icon: '○', color: 'rgba(255,255,255,0.3)' },
};

function getStatusConfig(status: string) {
  return STATUS_CONFIG[status] || { icon: '📋', color: '#64D2FF' };
}

function getImpactConfig(impact: string | null) {
  if (!impact) return IMPACT_CONFIG.none;
  return IMPACT_CONFIG[impact] || IMPACT_CONFIG.none;
}

function formatShortDate(dateString: string | null): string {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}.${month} ${hours}:${minutes}`;
  } catch {
    return '';
  }
}

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 1) + '…';
}

// Stats pill component
function StatsPill({
  category,
  count,
  isActive,
  onClick,
}: {
  category: typeof CATEGORIES[number];
  count: number;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className={`stats-pill ${isActive ? 'stats-pill--active' : ''}`}
      onClick={onClick}
    >
      <span className="stats-pill-icon">{category.icon}</span>
      <span className="stats-pill-count">{count}</span>
    </button>
  );
}

// Request card component
function RequestCard({ request, index }: { request: RequestDisplayItem; index: number }) {
  const navigate = useNavigate();
  const statusConfig = getStatusConfig(request.status);
  const impactConfig = getImpactConfig(request.impact);

  const handleClick = () => {
    hapticFeedback('light');
    navigate(`/request/${request.id}`);
  };

  return (
    <div
      className="request-card animate-slide-up"
      style={{ animationDelay: `${index * 40}ms` }}
      onClick={handleClick}
    >
      <div
        className="request-card-icon"
        style={{ background: `${statusConfig.color}15`, borderColor: `${statusConfig.color}30` }}
      >
        <span>{statusConfig.icon}</span>
      </div>
      <div className="request-card-content">
        <div className="request-card-header">
          <span className="request-card-id">#{request.id}</span>
          <span className="request-card-urgency" style={{ color: impactConfig.color }}>
            {impactConfig.icon}
          </span>
        </div>
        <div className="request-card-title">{truncateText(request.subject, 60)}</div>
        <div className="request-card-meta">
          {request.category && <span>{request.category}</span>}
          {request.updatedAt && (
            <>
              <span className="request-card-dot">•</span>
              <span>{formatShortDate(request.updatedAt)}</span>
            </>
          )}
        </div>
      </div>
      <div className="request-card-chevron">›</div>
    </div>
  );
}

export function HomePage() {
  const [activeCategory, setActiveCategory] = useState('needs_response');
  const [page, setPage] = useState(1);
  const [accumulated, setAccumulated] = useState<RequestDisplayItem[]>([]);
  const navigate = useNavigate();

  const { data: stats, isLoading: statsLoading } = useStats();
  const {
    data: categoryData,
    isLoading: categoryLoading,
    isError: categoryError,
    refetch: refetchCategory,
  } = useRequestsByCategory(activeCategory, { limit: 50, page });
  // enabled-гейт: без него запрос category=all дублировал каждый запрос
  // категории, хотя его результат не рендерился
  const {
    data: allData,
    isLoading: allLoading,
    isError: allError,
    refetch: refetchAll,
  } = useRequests({ limit: 50, category: 'all', page }, { enabled: activeCategory === 'all' });

  const activeData = activeCategory === 'all' ? allData : categoryData;
  const isLoading = activeCategory === 'all' ? allLoading : categoryLoading;
  const isError = activeCategory === 'all' ? allError : categoryError;
  const refetchActive = activeCategory === 'all' ? refetchAll : refetchCategory;

  // Накапливаем страницы: page=1 заменяет список, следующие - дополняют.
  // Рефетч страницы (фокус окна) не должен ни дублировать карточки,
  // ни терять обновлённые поля - совпавшие id заменяем свежими данными
  useEffect(() => {
    if (!activeData) return;
    setAccumulated((prev) => {
      if (page === 1) return activeData.data;
      const incoming = new Map(activeData.data.map((r) => [r.id, r]));
      const refreshed = prev.map((r) => incoming.get(r.id) ?? r);
      const known = new Set(prev.map((r) => r.id));
      return [...refreshed, ...activeData.data.filter((r) => !known.has(r.id))];
    });
  }, [activeData, page]);

  const requests = accumulated;
  const hasMore = !!activeData?.meta && page < activeData.meta.totalPages;

  const handleCategoryClick = useCallback((category: string) => {
    // Повторный клик по активной категории не должен очищать список:
    // зависимости эффекта не меняются, и он бы не восстановил данные
    if (category === activeCategory) return;
    hapticFeedback('light');
    setActiveCategory(category);
    setPage(1);
    setAccumulated([]);
  }, [activeCategory]);

  const handleLoadMore = useCallback(() => {
    // Повторный тап во время загрузки перескочил бы через страницу,
    // и пропущенная страница никогда не попала бы в накопленный список
    if (isLoading) return;
    hapticFeedback('light');
    if (isError) {
      // Упавшая страница: повторяем её же, а не переходим к следующей
      refetchActive();
      return;
    }
    setPage((p) => p + 1);
  }, [isLoading, isError, refetchActive]);

  if (statsLoading) {
    return (
      <div className="home-loading">
        <Spinner size="l" />
      </div>
    );
  }

  const activeConfig = CATEGORIES.find(c => c.id === activeCategory);

  return (
    <div className="home-page">
      {/* Header */}
      <header className="home-header">
        <h1 className="home-title">Мои заявки</h1>
        {stats && (
          <div className="home-stats">
            {CATEGORIES.map((cat) => (
              <StatsPill
                key={cat.id}
                category={cat}
                count={getCategoryCount(stats, cat.id)}
                isActive={activeCategory === cat.id}
                onClick={() => handleCategoryClick(cat.id)}
              />
            ))}
          </div>
        )}
      </header>

      {/* Section header */}
      <div className="home-section-header">
        <span className="home-section-icon">{activeConfig?.icon}</span>
        <span className="home-section-title">{activeConfig?.label}</span>
        {requests && requests.length > 0 && (
          <span className="home-section-count">{requests.length}</span>
        )}
      </div>

      {/* Request list */}
      <div className="home-content">
        {isLoading && requests.length === 0 ? (
          <div className="home-loading-inline">
            <Spinner size="m" />
          </div>
        ) : requests && requests.length > 0 ? (
          <div className="home-requests">
            {requests.map((request, index) => (
              <RequestCard key={request.id} request={request} index={index} />
            ))}
            {(hasMore || isLoading || isError) && (
              <div style={{ padding: '12px 0', textAlign: 'center' }}>
                <Button
                  size="m"
                  mode="bezeled"
                  stretched
                  disabled={isLoading}
                  onClick={handleLoadMore}
                >
                  {isLoading ? 'Загрузка…' : isError ? 'Повторить' : 'Загрузить ещё'}
                </Button>
              </div>
            )}
          </div>
        ) : isError ? (
          <div className="home-empty">
            <div className="home-empty-icon">⚠️</div>
            <p className="home-empty-text">Не удалось загрузить заявки</p>
            <Button size="m" mode="bezeled" onClick={() => refetchActive()}>
              Повторить
            </Button>
          </div>
        ) : (
          <div className="home-empty">
            <div className="home-empty-icon">
              {activeConfig?.icon || '📋'}
            </div>
            <p className="home-empty-text">{getEmptyMessage(activeCategory)}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function getEmptyMessage(category: string): string {
  switch (category) {
    case 'needs_response':
      return 'Нет заявок, требующих реакции';
    case 'in_progress':
      return 'Нет заявок в работе';
    case 'waiting':
      return 'Нет заявок в ожидании';
    case 'all':
      return 'Нет открытых заявок';
    default:
      return 'Заявки не найдены';
  }
}
